import { VA_FORM_IDS } from '@department-of-veterans-affairs/platform-forms/constants';
import { TRACKING_526EZ_SIDENAV_CLICKS } from '../../../constants';
import {
  trackAction,
  incrementClickCounter,
  getClickCounts,
  getTrackingDefaults,
} from '../../../../shared/utils/tracking/datadogRumTracking';

/**
 * Returns side-nav related submission properties used by submission orchestrators.
 * Keep this scoped to side-nav concerns only; the submit orchestrator merges
 * this subset with other feature subsets.
 *
 * Properties include:
 * - sourcePath: the URL path of the page where the user submitted (from side-nav tracking)
 * - backButtonClickCount: how many times the user clicked "Back" in the side nav during this session (from side-nav tracking)
 * - continueButtonClickCount: how many times the user clicked "Continue" in the side nav during this session (from side-nav tracking)
 * - sideNavClickCount: how many times the user clicked any chapter in the side nav during this session (from side-nav tracking; only included if > 0 to reduce noise)
 *
 * @returns {object}
 */
export const getSideNavSubmissionProperties = () => {
  const { sourcePath, sidenav526ezEnabled } = getTrackingDefaults();
  const properties = {
    sourcePath,
    ...getClickCounts(),
  };

  if (sidenav526ezEnabled !== undefined) {
    properties.sidenav526ezEnabled = sidenav526ezEnabled;
  }

  return properties;
};

/**
 * Tracks side nav chapter clicks
 * This tracks when users navigate via the side navigation menu
 * Maintains a session-based click counter shared with mobile accordion clicks
 *
 * @param {object} params - Parameters for tracking
 * @param {object} params.pageData - Page data including key, label, and path
 * @param {string} params.pathname - Current page pathname before navigation
 */
export const trackSideNavChapterClick = (params = {}) => {
  const { pageData = {}, pathname = '' } = params;
  incrementClickCounter(TRACKING_526EZ_SIDENAV_CLICKS);

  const properties = {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    chapterTitle: pageData?.label || '',
    sourcePath: pathname,
    ...getClickCounts(),
  };

  trackAction('Side navigation - Chapter clicked', properties);
};

/**
 * Tracks mobile sidenav accordion expand/collapse
 * This tracks when users interact with the mobile accordion to show/hide navigation
 * Maintains a session-based click counter shared with chapter clicks
 *
 * @param {object} params - Parameters for tracking
 * @param {string} params.pathname - Current page pathname
 * @param {string} params.state - Accordion state: 'expanded' or 'collapsed'
 * @param {string} params.accordionTitle - Title of the accordion (e.g., "Form steps")
 */
export const trackMobileAccordionClick = ({
  pathname = '',
  state = '',
  accordionTitle = '',
} = {}) => {
  incrementClickCounter(TRACKING_526EZ_SIDENAV_CLICKS);

  const properties = {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    state,
    accordionTitle,
    sourcePath: pathname,
    ...getClickCounts(),
  };

  trackAction('Side navigation - Mobile accordion clicked', properties);
};

/**
 * Tracks when a side nav chapter reset occurs due to a high-impact field change.
 *
 * @param {object} params - Parameters for tracking
 * @param {string} params.pathname - Current page pathname at time of reset
 * @param {number} params.fromChapterIndex - Previous chapter index before reset
 * @param {number} params.toChapterIndex - Chapter index after reset
 * @param {string} params.conditionsExperienceUsed - 'v2' or 'v1' depending on isNewConditionsOn
 */
export const trackSideNavReset = ({
  pathname,
  fromChapterIndex,
  toChapterIndex,
  conditionsExperienceUsed,
} = {}) => {
  const baseProperties = getSideNavSubmissionProperties();
  const properties = {
    ...baseProperties,
    formId: VA_FORM_IDS.FORM_21_526EZ,
    sourcePath: pathname,
    conditionsExperienceUsed,
  };

  // Only include chapter indices if both are numbers
  if (
    typeof fromChapterIndex === 'number' &&
    typeof toChapterIndex === 'number'
  ) {
    properties.fromChapterIndex = fromChapterIndex;
    properties.toChapterIndex = toChapterIndex;
  }

  trackAction('21-526EZ_sideNavProgressReset', properties);
};

/**
 * Tracks when a side nav chapter reset is restored (undone) due to high-impact field change being undone.
 *
 * @param {object} params - Parameters for tracking
 * @param {string} params.pathname - Current page pathname at time of restore
 * @param {number} params.fromChapterIndex - Current chapter index before restore
 * @param {number} params.toChapterIndex - Chapter index after restore
 * @param {string} params.conditionsExperienceUsed - 'v2' or 'v1' depending on isNewConditionsOn
 */
export const trackSideNavRestored = ({
  pathname,
  fromChapterIndex,
  toChapterIndex,
  conditionsExperienceUsed,
} = {}) => {
  const baseProperties = getSideNavSubmissionProperties();
  const properties = {
    ...baseProperties,
    formId: VA_FORM_IDS.FORM_21_526EZ,
    sourcePath: pathname,
    conditionsExperienceUsed,
  };

  // Only include chapter indices if both are numbers
  if (
    typeof fromChapterIndex === 'number' &&
    typeof toChapterIndex === 'number'
  ) {
    properties.fromChapterIndex = fromChapterIndex;
    properties.toChapterIndex = toChapterIndex;
  }

  trackAction('21-526EZ_sideNavProgressRestored', properties);
};

/**
 * Tracks when the reset alert is shown on the Conditions orientation page.
 *
 * @param {object} params - Parameters for tracking
 * @param {string} params.pathname - Current page pathname (conditions/orientation)
 * @param {number} params.maxChapterIndex - Current max chapter index value
 * @param {string} params.conditionsExperienceUsed - 'v2' or 'v1' depending on isNewConditionsOn
 */
export const trackSideNavResetAlertShown = ({
  pathname,
  maxChapterIndex,
  conditionsExperienceUsed,
} = {}) => {
  const baseProperties = getSideNavSubmissionProperties();
  const properties = {
    ...baseProperties,
    formId: VA_FORM_IDS.FORM_21_526EZ,
    sourcePath: pathname,
    conditionsExperienceUsed,
  };

  // Only include maxChapterIndex if it is a number
  if (typeof maxChapterIndex === 'number') {
    properties.maxChapterIndex = maxChapterIndex;
  }

  trackAction('21-526EZ_sideNavResetAlertShown', properties);
};
